import { z } from "zod/v4";

import { SubscriptionBillingProviderList } from "../../subscriptions";

import {
  BillingAdminScheduleScenarioList,
  BillingAgencyLifecycleScenarioList,
  BillingCadenceList,
  BillingCreateReactivateScenarioList,
  BillingEffectiveTimingList,
  BillingPauseCancelReleaseActionList,
  BillingPauseCancelReleaseScenarioList,
  type BillingPolicyCommandFamily,
  BillingPolicyCommandFamilyList,
  BillingProductSetChangeScenarioList,
  BillingProductSetChangeTypeList,
  BillingRequestSource,
  BillingRequestSourceList,
  BillingScheduledFollowupCommandTypeList,
  BillingScheduledWakeupScenarioList,
  BillingSubscriptionLifecycleReviewTypeList,
  BillingSubscriptionLifecycleScenarioList,
  BillingTrialCommandTypeList,
  BillingTrialLifecycleScenarioList,
} from "./catalog";
import {
  AgencyLifecyclePayloadSchema,
  BillingActiveSubscriptionSnapshotRefSchema,
  BillingAdminPhaseSchema,
  BillingAdminSettingsSchema,
  BillingCommandPayloadV1,
  BillingFollowupPayloadSchema,
  BillingModuleTrialMarkerSchema,
  BillingProductRefSchema,
  BillingProductSetTargetSchema,
  BillingRegimeConversionContextSchema,
  BillingTierSnapshotRefSchema,
  BillingTrialStateSchema,
  ModuleTrialConfirmMarkerSchema,
  ModuleTrialRevokeMarkerSchema,
  ModuleTrialUnconfirmMarkerSchema,
  ProviderLifecycleActionSchema,
} from "./payloads";
import { BillingPlanIntentRefV1Schema } from "./plan-intent-ref";
import { BillingRuntimeCheckpointV1Schema } from "./runtime";
import { billingStateAssertionError } from "./state-assertion-error";

export const CreateReactivationBillingIntentSelectionInputV1Schema = z.object({
  commandFamily: z.literal("create_reactivate"),
  scenario: z.enum(BillingCreateReactivateScenarioList),
  commandId: z.string().min(1),
  orgId: z.string().min(1),
  // The request source at the root of a repair/replay lineage, which is how
  // the merchant originally asked and so decides the billing provider.
  originRequestSource: z.enum(BillingRequestSourceList),
  requestedPlanIntentId: z.uuid().optional(),
  requestedProductSetTarget: BillingProductSetTargetSchema,
  requestedCadence: z.enum(BillingCadenceList),
  trialDaysRequested: z.number().nullish(),
  billingProviderSelection: z.enum(SubscriptionBillingProviderList).optional(),
  existingBillingProvider: z.enum(SubscriptionBillingProviderList).optional(),
  stripeSubscriptionLinkId: z.uuid().optional(),
});

export const TrialLifecycleBillingIntentSelectionInputV1Schema = z.object({
  commandFamily: z.literal("trial_lifecycle"),
  scenario: z.enum(BillingTrialLifecycleScenarioList),
  commandId: z.string().min(1),
  orgId: z.string().min(1),
  trialCommandType: z.enum(BillingTrialCommandTypeList),
  activeProducts: z.array(BillingProductRefSchema).optional(),
  trialState: BillingTrialStateSchema.optional(),
  resolvedBillingProvider: z.enum(SubscriptionBillingProviderList),
});

export const ProductSetChangeBillingIntentSelectionInputV1Schema = z.object({
  commandFamily: z.literal("product_set_change"),
  scenario: z.enum(BillingProductSetChangeScenarioList),
  commandId: z.string().min(1),
  orgId: z.string().min(1),
  requestedPlanIntentId: z.uuid().optional(),
  // Absent on `cancel_pending_module_removal`,
  // `cancel_pending_regime_conversion` and `apply_coupon`, which change no
  // product set.
  changeType: z.enum(BillingProductSetChangeTypeList).optional(),
  // The booked command whose scheduled-wakeup booking the release revokes.
  // Present only on `cancel_pending_module_removal` and
  // `cancel_pending_regime_conversion`.
  sourceCommandId: z.string().min(1).optional(),
  requestedProductSetTarget: BillingProductSetTargetSchema.optional(),
  requestedEffectiveTiming: z.enum(BillingEffectiveTimingList),
  resolvedBillingProvider: z.enum(SubscriptionBillingProviderList),
  // Operator-authored order-volume quantity for a modular Shopify bridge.
  // Admin-origin only: the self-serve command schema does not carry it, and
  // policy resolution honors it solely for admin-sourced commands.
  orderVolumeQuantity: z.number().int().positive().optional(),
  // Which linked subscription the change was requested against. Optional for
  // legacy in-flight commands; without it fact loading falls back to the
  // org's primary live sub.
  stripeSubscriptionLinkId: z.uuid().optional(),
  moduleTrials: BillingModuleTrialMarkerSchema.array().min(1).optional(),
  moduleTrialConfirm: ModuleTrialConfirmMarkerSchema.array().min(1).optional(),
  moduleTrialRevoke: ModuleTrialRevokeMarkerSchema.array().min(1).optional(),
  moduleTrialUnconfirm: ModuleTrialUnconfirmMarkerSchema.array()
    .min(1)
    .optional(),
  // Reject a pre-multi singular marker instead of silently stripping it — a
  // stripped marker degrades the grant to a billed add (ENG-6669).
  moduleTrial: z.never().optional(),
});

export const PauseCancelReleaseBillingIntentSelectionInputV1Schema = z.object({
  commandFamily: z.literal("pause_cancel_release"),
  scenario: z.enum(BillingPauseCancelReleaseScenarioList),
  commandId: z.string().min(1),
  orgId: z.string().min(1),
  lifecycleAction: z.enum(BillingPauseCancelReleaseActionList),
  activeSubscriptionSnapshot:
    BillingActiveSubscriptionSnapshotRefSchema.optional(),
  resolvedBillingProvider: z.enum(SubscriptionBillingProviderList),
  providerLifecycle: ProviderLifecycleActionSchema.optional(),
  // A request, not a timing: the pause lands on the first period end at or
  // after it.
  pauseAt: z.number().optional(),
  cancelAt: z.number().optional(),
  // The parked pause whose scheduled-wakeup booking the undo releases. Present
  // only on `cancel_pending_pause`.
  sourceCommandId: z.string().min(1).optional(),
});

export const SubscriptionLifecycleBillingIntentSelectionInputV1Schema =
  z.object({
    commandFamily: z.literal("subscription_lifecycle"),
    scenario: z.enum(BillingSubscriptionLifecycleScenarioList),
    commandId: z.string().min(1),
    orgId: z.string().min(1),
    lifecycleReviewType: z.enum(BillingSubscriptionLifecycleReviewTypeList),
    currentTierSnapshot: BillingTierSnapshotRefSchema.optional(),
    currentCadence: z.enum(BillingCadenceList).optional(),
    resolvedBillingProvider: z.enum(SubscriptionBillingProviderList),
  });

export const ScheduledWakeupBillingIntentSelectionInputV1Schema = z.object({
  commandFamily: z.literal("scheduled_wakeup"),
  scenario: z.enum(BillingScheduledWakeupScenarioList),
  commandId: z.string().min(1),
  orgId: z.string().min(1),
  delayedJobId: z.string().min(1),
  queueName: z.string().min(1),
  dueAt: z.string(),
  followupCommandType: z.enum(BillingScheduledFollowupCommandTypeList),
  resumeCommandFamily: z.enum(BillingPolicyCommandFamilyList),
  resumeScenario: z.string().min(1),
  followupPayload: BillingFollowupPayloadSchema.optional(),
  resolvedBillingProvider: z.enum(SubscriptionBillingProviderList),
  planIntentRef: BillingPlanIntentRefV1Schema.optional(),
});

export const AdminScheduleBillingIntentSelectionInputV1Schema = z.object({
  commandFamily: z.literal("admin_schedule"),
  scenario: z.enum(BillingAdminScheduleScenarioList),
  commandId: z.string().min(1),
  orgId: z.string().min(1),
  requestedPlanIntentId: z.uuid().optional(),
  requestedProductSetTarget: BillingProductSetTargetSchema,
  requestedCadence: z.enum(BillingCadenceList),
  customerId: z.string().min(1),
  agencyId: z.string().nullish(),
  stripeSubscriptionId: z.string().min(1).optional(),
  subscriptionLinkIds: z.array(z.string().min(1)).min(1).optional(),
  billingProvider: z.enum(SubscriptionBillingProviderList),
  billingProviderSelection: z.enum(SubscriptionBillingProviderList).optional(),
  settings: BillingAdminSettingsSchema,
  phases: z.array(BillingAdminPhaseSchema).min(1),
  billingRegimeConversion: BillingRegimeConversionContextSchema.optional(),
});

export const AgencyLifecycleBillingIntentSelectionInputV1Schema = z.object({
  commandFamily: z.literal("agency_lifecycle"),
  scenario: z.enum(BillingAgencyLifecycleScenarioList),
  commandId: z.string().min(1),
  orgId: z.string().min(1),
  agencyLifecycle: AgencyLifecyclePayloadSchema,
  resolvedBillingProvider: z.literal("stripe"),
});

export const BillingNormalizedIntentSelectionInputV1Schema =
  z.discriminatedUnion("commandFamily", [
    CreateReactivationBillingIntentSelectionInputV1Schema,
    TrialLifecycleBillingIntentSelectionInputV1Schema,
    ProductSetChangeBillingIntentSelectionInputV1Schema,
    PauseCancelReleaseBillingIntentSelectionInputV1Schema,
    SubscriptionLifecycleBillingIntentSelectionInputV1Schema,
    ScheduledWakeupBillingIntentSelectionInputV1Schema,
    AdminScheduleBillingIntentSelectionInputV1Schema,
    AgencyLifecycleBillingIntentSelectionInputV1Schema,
  ]);

export type BillingNormalizedIntentSelectionInputV1 = z.infer<
  typeof BillingNormalizedIntentSelectionInputV1Schema
>;

export type BillingNormalizedIntentInputV1<
  TCommandFamily extends BillingPolicyCommandFamily =
    BillingPolicyCommandFamily,
> = {
  commandId: string;
  orgId: string;
  requestSource: BillingRequestSource;
  payload: BillingCommandPayloadV1<TCommandFamily>;
};

export const CreateReactivationBillingIntentV1Schema = z.object({
  schemaVersion: z.literal("v1"),
  commandFamily: z.literal("create_reactivate"),
  scenario: z.enum(BillingCreateReactivateScenarioList),
  commandId: z.string().min(1),
  orgId: z.string().min(1),
  resolvedBillingProvider: z.enum(SubscriptionBillingProviderList),
  workflowCheckpointJson: BillingRuntimeCheckpointV1Schema,
  planIntentRef: BillingPlanIntentRefV1Schema.optional(),
  intentPayload: z.object({
    scenario: z.enum(BillingCreateReactivateScenarioList),
    requestedPlanIntentId: z.uuid().optional(),
    requestedProductSetTarget: BillingProductSetTargetSchema,
    requestedCadence: z.enum(BillingCadenceList),
    trialDaysRequested: z.number().nullish(),
    billingProviderSelection: z
      .enum(SubscriptionBillingProviderList)
      .optional(),
    stripeSubscriptionLinkId: z.uuid().optional(),
  }),
});

export type CreateReactivationBillingIntentV1 = z.infer<
  typeof CreateReactivationBillingIntentV1Schema
>;

export const TrialLifecycleBillingIntentV1Schema = z.object({
  schemaVersion: z.literal("v1"),
  commandFamily: z.literal("trial_lifecycle"),
  scenario: z.enum(BillingTrialLifecycleScenarioList),
  commandId: z.string().min(1),
  orgId: z.string().min(1),
  resolvedBillingProvider: z.enum(SubscriptionBillingProviderList),
  workflowCheckpointJson: BillingRuntimeCheckpointV1Schema,
  intentPayload: z.object({
    scenario: z.enum(BillingTrialLifecycleScenarioList),
    trialCommandType: z.enum(BillingTrialCommandTypeList),
    activeProducts: z.array(BillingProductRefSchema).optional(),
    trialState: BillingTrialStateSchema.optional(),
  }),
});

export const ProductSetChangeBillingIntentV1Schema = z.object({
  schemaVersion: z.literal("v1"),
  commandFamily: z.literal("product_set_change"),
  scenario: z.enum(BillingProductSetChangeScenarioList),
  commandId: z.string().min(1),
  orgId: z.string().min(1),
  resolvedBillingProvider: z.enum(SubscriptionBillingProviderList),
  workflowCheckpointJson: BillingRuntimeCheckpointV1Schema,
  planIntentRef: BillingPlanIntentRefV1Schema.optional(),
  intentPayload: z.object({
    scenario: z.enum(BillingProductSetChangeScenarioList),
    requestedPlanIntentId: z.uuid().optional(),
    // Absent on `cancel_pending_module_removal` and
    // `cancel_pending_regime_conversion`, which change no product set.
    changeType: z.enum(BillingProductSetChangeTypeList).optional(),
    // The booked command whose scheduled-wakeup booking the release revokes.
    // Present only on `cancel_pending_module_removal` and
    // `cancel_pending_regime_conversion`.
    sourceCommandId: z.string().min(1).optional(),
    requestedProductSetTarget: BillingProductSetTargetSchema.optional(),
    requestedEffectiveTiming: z.enum(BillingEffectiveTimingList).optional(),
    // Which linked subscription the change was requested against. Optional for
    // legacy in-flight commands; without it fact loading falls back to the
    // org's primary live sub.
    stripeSubscriptionLinkId: z.uuid().optional(),
    moduleTrials: BillingModuleTrialMarkerSchema.array().min(1).optional(),
    moduleTrialConfirm: ModuleTrialConfirmMarkerSchema.array()
      .min(1)
      .optional(),
    moduleTrialRevoke: ModuleTrialRevokeMarkerSchema.array().min(1).optional(),
    moduleTrialUnconfirm: ModuleTrialUnconfirmMarkerSchema.array()
      .min(1)
      .optional(),
    // Reject a pre-multi singular marker instead of silently stripping it — a
    // stripped marker degrades the grant to a billed add (ENG-6669).
    moduleTrial: z.never().optional(),
    // Subscription items whose Module Trial just settled. The subscription
    // cache may still show them trialing when the settlement re-resolves the
    // plan, and a trialing item is excluded from the bundle tier.
    settledTrialSubscriptionItemIds: z
      .string()
      .min(1)
      .array()
      .min(1)
      .optional(),
    // Operator-authored order-volume quantity for a modular Shopify bridge.
    // Only the admin adjust-billing lane writes it; self-serve normalization
    // never sets it.
    orderVolumeQuantity: z.number().int().positive().optional(),
  }),
});

export const PauseCancelReleaseBillingIntentV1Schema = z.object({
  schemaVersion: z.literal("v1"),
  commandFamily: z.literal("pause_cancel_release"),
  scenario: z.enum(BillingPauseCancelReleaseScenarioList),
  commandId: z.string().min(1),
  orgId: z.string().min(1),
  resolvedBillingProvider: z.enum(SubscriptionBillingProviderList),
  workflowCheckpointJson: BillingRuntimeCheckpointV1Schema,
  planIntentRef: BillingPlanIntentRefV1Schema.optional(),
  intentPayload: z.object({
    scenario: z.enum(BillingPauseCancelReleaseScenarioList),
    lifecycleAction: z.enum(BillingPauseCancelReleaseActionList),
    activeSubscriptionSnapshot:
      BillingActiveSubscriptionSnapshotRefSchema.optional(),
    providerLifecycle: ProviderLifecycleActionSchema.optional(),
    // A request, not a timing: the pause lands on the first period end at or
    // after it.
    pauseAt: z.number().optional(),
    cancelAt: z.number().optional(),
    // The parked pause whose scheduled-wakeup booking the undo releases.
    // Present only on `cancel_pending_pause`.
    sourceCommandId: z.string().min(1).optional(),
  }),
});

export const SubscriptionLifecycleBillingIntentV1Schema = z.object({
  schemaVersion: z.literal("v1"),
  commandFamily: z.literal("subscription_lifecycle"),
  scenario: z.enum(BillingSubscriptionLifecycleScenarioList),
  commandId: z.string().min(1),
  orgId: z.string().min(1),
  resolvedBillingProvider: z.enum(SubscriptionBillingProviderList),
  workflowCheckpointJson: BillingRuntimeCheckpointV1Schema,
  intentPayload: z.object({
    scenario: z.enum(BillingSubscriptionLifecycleScenarioList),
    lifecycleReviewType: z.enum(BillingSubscriptionLifecycleReviewTypeList),
    currentTierSnapshot: BillingTierSnapshotRefSchema.optional(),
    currentCadence: z.enum(BillingCadenceList).optional(),
  }),
});

export const ScheduledWakeupBillingIntentV1Schema = z.object({
  schemaVersion: z.literal("v1"),
  commandFamily: z.literal("scheduled_wakeup"),
  scenario: z.enum(BillingScheduledWakeupScenarioList),
  commandId: z.string().min(1),
  orgId: z.string().min(1),
  resolvedBillingProvider: z.enum(SubscriptionBillingProviderList),
  workflowCheckpointJson: BillingRuntimeCheckpointV1Schema,
  planIntentRef: BillingPlanIntentRefV1Schema.optional(),
  intentPayload: z.object({
    scenario: z.enum(BillingScheduledWakeupScenarioList),
    delayedJobId: z.string().min(1),
    followupCommandType: z.enum(BillingScheduledFollowupCommandTypeList),
    resumeCommandFamily: z.enum(BillingPolicyCommandFamilyList),
    resumeScenario: z.string().min(1),
    followupPayload: BillingFollowupPayloadSchema.optional(),
  }),
});

export const AdminScheduleBillingIntentV1Schema = z.object({
  schemaVersion: z.literal("v1"),
  commandFamily: z.literal("admin_schedule"),
  scenario: z.enum(BillingAdminScheduleScenarioList),
  commandId: z.string().min(1),
  orgId: z.string().min(1),
  resolvedBillingProvider: z.enum(SubscriptionBillingProviderList),
  workflowCheckpointJson: BillingRuntimeCheckpointV1Schema,
  planIntentRef: BillingPlanIntentRefV1Schema.optional(),
  intentPayload: z.object({
    scenario: z.enum(BillingAdminScheduleScenarioList),
    requestedPlanIntentId: z.uuid().optional(),
    customerId: z.string().min(1),
    agencyId: z.string().nullish(),
    stripeSubscriptionId: z.string().min(1).optional(),
    subscriptionLinkIds: z.array(z.string().min(1)).min(1).optional(),
    requestedProductSetTarget: BillingProductSetTargetSchema,
    requestedCadence: z.enum(BillingCadenceList),
    settings: BillingAdminSettingsSchema,
    phases: z.array(BillingAdminPhaseSchema).min(1),
    billingRegimeConversion: BillingRegimeConversionContextSchema.optional(),
  }),
});

export const AgencyLifecycleBillingIntentV1Schema = z.object({
  schemaVersion: z.literal("v1"),
  commandFamily: z.literal("agency_lifecycle"),
  scenario: z.enum(BillingAgencyLifecycleScenarioList),
  commandId: z.string().min(1),
  orgId: z.string().min(1),
  resolvedBillingProvider: z.literal("stripe"),
  workflowCheckpointJson: BillingRuntimeCheckpointV1Schema,
  intentPayload: z.object({
    scenario: z.enum(BillingAgencyLifecycleScenarioList),
    agencyLifecycle: AgencyLifecyclePayloadSchema,
  }),
});

export const BillingNormalizedIntentV1Schema = z.discriminatedUnion(
  "commandFamily",
  [
    CreateReactivationBillingIntentV1Schema,
    TrialLifecycleBillingIntentV1Schema,
    ProductSetChangeBillingIntentV1Schema,
    PauseCancelReleaseBillingIntentV1Schema,
    SubscriptionLifecycleBillingIntentV1Schema,
    ScheduledWakeupBillingIntentV1Schema,
    AdminScheduleBillingIntentV1Schema,
    AgencyLifecycleBillingIntentV1Schema,
  ]
);

export type BillingNormalizedIntentV1 = z.infer<
  typeof BillingNormalizedIntentV1Schema
>;

export function isBillingNormalizedIntent(
  value: unknown
): value is BillingNormalizedIntentV1 {
  return BillingNormalizedIntentV1Schema.safeParse(value).success;
}

export function assertBillingNormalizedIntent(
  value: unknown
): asserts value is BillingNormalizedIntentV1 {
  const parsed = BillingNormalizedIntentV1Schema.safeParse(value);
  if (!parsed.success) {
    throw billingStateAssertionError(
      "billing normalized intent is missing required fields",
      parsed.error
    );
  }
}

export type TrialLifecycleBillingIntentV1 = z.infer<
  typeof TrialLifecycleBillingIntentV1Schema
>;

export type ProductSetChangeBillingIntentV1 = z.infer<
  typeof ProductSetChangeBillingIntentV1Schema
>;

export type PauseCancelReleaseBillingIntentV1 = z.infer<
  typeof PauseCancelReleaseBillingIntentV1Schema
>;

export type SubscriptionLifecycleBillingIntentV1 = z.infer<
  typeof SubscriptionLifecycleBillingIntentV1Schema
>;

export type ScheduledWakeupBillingIntentV1 = z.infer<
  typeof ScheduledWakeupBillingIntentV1Schema
>;

export type AdminScheduleBillingIntentV1 = z.infer<
  typeof AdminScheduleBillingIntentV1Schema
>;

export type AgencyLifecycleBillingIntentV1 = z.infer<
  typeof AgencyLifecycleBillingIntentV1Schema
>;

export type CreateReactivationBillingIntentSelectionInputV1 = z.infer<
  typeof CreateReactivationBillingIntentSelectionInputV1Schema
>;

export type TrialLifecycleBillingIntentSelectionInputV1 = z.infer<
  typeof TrialLifecycleBillingIntentSelectionInputV1Schema
>;

export type ProductSetChangeBillingIntentSelectionInputV1 = z.infer<
  typeof ProductSetChangeBillingIntentSelectionInputV1Schema
>;

export type PauseCancelReleaseBillingIntentSelectionInputV1 = z.infer<
  typeof PauseCancelReleaseBillingIntentSelectionInputV1Schema
>;

export type SubscriptionLifecycleBillingIntentSelectionInputV1 = z.infer<
  typeof SubscriptionLifecycleBillingIntentSelectionInputV1Schema
>;

export type ScheduledWakeupBillingIntentSelectionInputV1 = z.infer<
  typeof ScheduledWakeupBillingIntentSelectionInputV1Schema
>;

export type AdminScheduleBillingIntentSelectionInputV1 = z.infer<
  typeof AdminScheduleBillingIntentSelectionInputV1Schema
>;

export type AgencyLifecycleBillingIntentSelectionInputV1 = z.infer<
  typeof AgencyLifecycleBillingIntentSelectionInputV1Schema
>;
